import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  Info,
  Star,
} from "lucide-react";
import { formatZonedTime } from "../../../lib/time";
import { hasUnacknowledgedRefIds } from "../utils/slot-visuals.js";
import { computePopoverPosition } from "../utils/popover-position.js";
import { useEventTitles } from "../../calendar-enrichment/useEventTitles.js";
import { compoundEventKey } from "../../calendar-enrichment/providers/types.js";

// Privacy (calendar-cache-privacy P2): the engine no longer persists event
// titles in voteAutoReason. The component asks the enrichment hook for the
// title from (provider, calendarId, eventId) and renders a structural
// placeholder until it resolves. Reasons emitted before P2 may still carry
// the legacy `title` field on event entries — we accept that as a fallback
// (read-only; future engine reevals will overwrite the doc with the
// title-less shape).
const EVENT_TITLE_PLACEHOLDER = "Calendar event";

// Popover dimensions for this indicator's portal popover (18rem ≈ w-72).
// Positioning math is the shared `computePopoverPosition` (audit B-9).
const POPOVER_WIDTH = 288;
const POPOVER_HEIGHT_ESTIMATE = 220; // worst-case for flip-decision math
const POPOVER_GAP = 8;
const POPOVER_EDGE = 8;

/**
 * Unified per-slot indicator that dispatches on `voteAutoReason.kind` and
 * subsumes the previous SlotConflictWarning. Renders a small icon-button
 * that opens a popover on hover/click; the popover lists the specific
 * sources (events, busy windows, marks) with deep links into /availability.
 *
 * Kind dispatch:
 *   - "conflict"        → ⚠ amber AlertTriangle. Lists conflicting events
 *                         and busy windows. Multi-source supports +N more.
 *   - "preferred-align" → ⭐ gold Star.   Names the mark or free-calendar
 *                         event whose Preferred level aligned the slot.
 *   - "feasible-align"  → ✓ green CheckCircle2. Same payload shape as
 *                         preferred-align, just FEASIBLE level.
 *   - "no-group-match"  → ⓘ sky Info. Explains why a mark didn't apply
 *                         (the mark's group scope didn't include this poll).
 *
 * If `kind` is missing (legacy Chunk-C-shape conflict reasons that lack
 * top-level kind but still have humanLabel + refId), we treat the reason
 * as a conflict and synthesize a single row from humanLabel + refId.
 *
 * Props:
 *   reason: voteAutoReason[slotId] (required for the indicator to render —
 *     callers should null-check before passing)
 *   schedulerId: string  (used in the +N-more deep-link)
 *   slotId: string       (used in the +N-more deep-link)
 *   displayTimeZone: string | null
 *   groupNames?: Record<groupId, name>  (used in no-group-match popover)
 *   testId?: string
 *   voteAutoSource?: string|null  voteAutoSource[slotId]. Combined with
 *     reason.kind === "conflict" + unacknowledged refIds drives the
 *     Chunk G6 urgent (red AlertTriangle + Restore/Confirm) treatment.
 *   onRestore?: (slotId) => void|Promise<void>  Click handler for Restore;
 *     only invoked when isUrgentOverride. The button is rendered but
 *     disabled when this prop is absent.
 *   onConfirm?: (slotId) => void|Promise<void>  Click handler for Confirm;
 *     same gating as onRestore.
 */
export function SlotIndicator({
  reason,
  schedulerId,
  slotId,
  displayTimeZone,
  groupNames = {},
  testId,
  voteAutoSource = null,
  onRestore,
  onConfirm,
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);
  // Set when the popover is opened/entered via keyboard so a layout effect can
  // move focus into the portaled dialog (the portal lives at document.body, so
  // Tab from the trigger can't reach it — focus must be moved programmatically).
  const focusInRef = useRef(false);

  // Move keyboard focus to the first interactive control inside the popover.
  // Used for keyboard activation only; hover/pointer opens never steal focus.
  function focusFirstInPopover() {
    const el = popoverRef.current?.querySelector(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (el) el.focus();
  }

  // Resolve the effective kind. Conflict-shape reasons emitted by the engine
  // always carry kind="conflict" post-E1. Pre-E1 reasons (legacy vote docs)
  // had no top-level kind on conflict cases — fall back to "conflict" when
  // we see the conflict-shape signature (events/busyWindows/humanLabel).
  const explicitKind = reason?.kind || null;
  const looksLikeConflict =
    !explicitKind &&
    (Array.isArray(reason?.events) ||
      Array.isArray(reason?.busyWindows) ||
      reason?.humanLabel != null);
  const kind = explicitKind || (looksLikeConflict ? "conflict" : null);

  // Click-outside + Esc to close. The popover is portaled to document.body,
  // so we accept clicks either inside the wrapper (trigger) OR inside the
  // popover itself; only clicks outside both close.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(ev) {
      const inWrapper =
        wrapperRef.current && wrapperRef.current.contains(ev.target);
      const inPopover =
        popoverRef.current && popoverRef.current.contains(ev.target);
      if (!inWrapper && !inPopover) {
        setOpen(false);
      }
    }
    function onKey(ev) {
      if (ev.key === "Escape") {
        setOpen(false);
        // Restore focus to the trigger so keyboard users aren't dumped to
        // <body> when the portaled dialog unmounts.
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // While the popover is open, recompute its position on resize and on any
  // ancestor scroll (capture-phase listener so RBC's overflow-scroll
  // ancestors trigger us). useLayoutEffect runs synchronously after DOM
  // mutations but before paint, so the popover's first frame renders at the
  // computed position rather than flashing at {0,0}.
  //
  // Deps: [open] only. Popover content is stable per-render; if content
  // could grow while open (e.g., engine reeval widens reason.events),
  // add a ResizeObserver on popoverRef and recompute then.
  useLayoutEffect(() => {
    if (!open) return undefined;
    function update() {
      setPosition(
        computePopoverPosition(triggerRef.current, {
          width: POPOVER_WIDTH,
          heightEstimate: POPOVER_HEIGHT_ESTIMATE,
          gap: POPOVER_GAP,
          edge: POPOVER_EDGE,
        })
      );
    }
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  // When opened via keyboard, move focus into the dialog after it mounts.
  useLayoutEffect(() => {
    if (open && focusInRef.current) {
      focusInRef.current = false;
      focusFirstInPopover();
    }
    if (!open) focusInRef.current = false;
  }, [open]);

  function cancelHoverClose() {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }
  function handleMouseEnter() {
    cancelHoverClose();
    setOpen(true);
  }
  function handleMouseLeave() {
    cancelHoverClose();
    hoverCloseTimerRef.current = setTimeout(() => setOpen(false), 150);
  }
  useEffect(() => () => cancelHoverClose(), []);

  // Privacy (P2): collect the enrichment requests for any event-bearing
  // entries in the reason payload, then resolve via the enrichment cache.
  // useMemo keeps the request array stable across renders so the hook's
  // signature dedup short-circuits.
  const enrichmentRequests = useMemo(() => {
    const reqs = [];
    if (reason?.events) {
      for (const e of reason.events) {
        if (e?.provider && e?.calendarId && e?.eventId) {
          reqs.push({
            provider: e.provider,
            calendarId: e.calendarId,
            eventId: e.eventId,
          });
        }
      }
    }
    if (
      reason?.event?.provider &&
      reason?.event?.calendarId &&
      reason?.event?.eventId
    ) {
      reqs.push({
        provider: reason.event.provider,
        calendarId: reason.event.calendarId,
        eventId: reason.event.eventId,
      });
    }
    return reqs;
  }, [reason]);
  const enrichedTitles = useEventTitles(enrichmentRequests);

  // Resolve a title from the enrichment cache, falling back to a legacy
  // `title` field on the entry (for pre-P2 vote docs that haven't been
  // re-evaluated yet). Returns null when nothing is resolved — callers are
  // responsible for deciding the placeholder.
  function resolveEventTitle(entry) {
    if (!entry) return null;
    if (entry.provider && entry.calendarId && entry.eventId) {
      const key = compoundEventKey(
        entry.provider,
        entry.calendarId,
        entry.eventId,
      );
      const enriched = enrichedTitles.get(key);
      if (enriched?.title) return enriched.title;
    }
    return entry.title || null;
  }

  if (!kind) return null;

  // Chunk G6: synthesize the urgent override state from voteAutoSource +
  // unacknowledged refIds. Engine never emits a separate urgent kind — UI
  // computes it locally so a future calendar-sync that introduces a new
  // refId immediately re-fires the red treatment without a server round-trip.
  const isUrgentOverride =
    kind === "conflict" &&
    voteAutoSource === "auto-conflict-override" &&
    hasUnacknowledgedRefIds(reason);

  const dispatchKind = isUrgentOverride ? "conflict-override-urgent" : kind;
  const variant = pickVariant(dispatchKind);
  const Icon = variant.Icon;
  const colorClass = variant.colorClass;

  const label = buildAriaLabel({
    kind: dispatchKind,
    reason,
    resolveEventTitle,
  });
  const previousManualVote = reason?.previousManualVote || null;

  const popoverContent = open ? (
    <div
      ref={popoverRef}
      // role="dialog" (not tooltip): the popover holds interactive controls
      // (deep-links, Restore/Confirm), which a tooltip must never contain.
      // Distinct, concise name (the trigger already carries the verbose label,
      // so don't duplicate it here).
      role="dialog"
      aria-label="Slot details"
      aria-modal="false"
      style={{
        position: "fixed",
        left: `${position.left}px`,
        top: `${position.top}px`,
        width: `${POPOVER_WIDTH}px`,
        zIndex: 50,
      }}
      // The portal popover lives outside the wrapper, so its own
      // mouseEnter/Leave drive the hover-grace timer to keep it open while
      // the user moves the cursor onto its content.
      onMouseEnter={cancelHoverClose}
      onMouseLeave={handleMouseLeave}
      // Close once focus leaves the dialog entirely (keyboard Tab past the last
      // control). Moving focus back to the trigger is handled by Escape; a Tab
      // exit just dismisses.
      onBlur={(e) => {
        if (
          popoverRef.current?.contains(e.relatedTarget) ||
          e.relatedTarget === triggerRef.current
        ) {
          return;
        }
        handleMouseLeave();
      }}
      className="rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-800"
    >
      {/* Chunk G6: red header announcing the engine cleared the user's
          manual vote. Sits above the existing ConflictBody so the user sees
          the *why* before the conflict list. */}
      {isUrgentOverride && (
        <p
          data-testid={`slot-indicator-urgent-header-${slotId}`}
          className="mb-2 rounded-md border-l-2 border-red-500 bg-red-50 px-2 py-1.5 text-[12px] font-semibold text-red-800 dark:border-red-400 dark:bg-red-900/30 dark:text-red-200"
        >
          ⚠ Engine cleared your {previousManualVote || "manual"} vote.
        </p>
      )}
      {kind === "no-group-match" ? (
        <GroupScopeBody reason={reason} groupNames={groupNames} />
      ) : kind === "conflict" ? (
        <ConflictBody
          reason={reason}
          schedulerId={schedulerId}
          slotId={slotId}
          displayTimeZone={displayTimeZone}
          resolveEventTitle={resolveEventTitle}
        />
      ) : (
        <AlignmentBody
          kind={kind}
          reason={reason}
          displayTimeZone={displayTimeZone}
          resolveEventTitle={resolveEventTitle}
        />
      )}
      {isUrgentOverride && (
        <div className="mt-3 flex gap-2 border-t border-slate-200 pt-2 dark:border-slate-700">
          <button
            type="button"
            data-testid={`slot-indicator-restore-${slotId}`}
            disabled={typeof onRestore !== "function"}
            // Stop propagation so the click-outside handler (mousedown) +
            // any RBC parent listeners don't intercept before the callback
            // runs. Without this, the popover closes via click-outside
            // before onRestore resolves.
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={async (ev) => {
              ev.stopPropagation();
              if (typeof onRestore === "function") {
                await onRestore(slotId);
                setOpen(false);
              }
            }}
            className="flex-1 rounded-md bg-brand-primary px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Restore my {previousManualVote || "vote"}
          </button>
          <button
            type="button"
            data-testid={`slot-indicator-confirm-${slotId}`}
            disabled={typeof onConfirm !== "function"}
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={async (ev) => {
              ev.stopPropagation();
              if (typeof onConfirm === "function") {
                await onConfirm(slotId);
                setOpen(false);
              }
            }}
            className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Keep cleared
          </button>
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      <span
        ref={wrapperRef}
        className="relative inline-flex items-center"
        data-testid={testId || `slot-indicator-${slotId}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          ref={triggerRef}
          type="button"
          // RBC's <Calendar onSelectEvent> fires on mousedown→click, so we stop
          // both phases here. Without this, opening the popover on a calendar
          // slot cell also opens the vote modal. Other consumers (list view,
          // vote-dialog) have no parent click handlers that rely on bubbling,
          // so stopping propagation universally is safe.
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            // ArrowDown moves focus INTO the dialog. The popover portals to
            // document.body, so Tab can't reach it — ArrowDown is the explicit
            // "enter the popup" gesture (as in menus/comboboxes). Enter/Space
            // keep their native button toggle via onClick.
            if (e.key === "ArrowDown") {
              e.preventDefault();
              e.stopPropagation();
              if (open) {
                focusFirstInPopover();
              } else {
                focusInRef.current = true;
                setOpen(true);
              }
            }
          }}
          className={`inline-flex h-5 w-5 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${colorClass}`}
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <Icon
            className="h-3.5 w-3.5"
            aria-hidden="true"
            data-testid={variant.iconTestId}
          />
        </button>
      </span>
      {popoverContent && createPortal(popoverContent, document.body)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Variant table
// ---------------------------------------------------------------------------

function pickVariant(kind) {
  switch (kind) {
    case "conflict-override-urgent":
      return {
        Icon: AlertTriangle,
        iconTestId: "warning-icon-urgent",
        colorClass:
          "text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30",
      };
    case "conflict":
      return {
        Icon: AlertTriangle,
        iconTestId: "warning-icon",
        colorClass:
          "text-amber-600 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-900/30",
      };
    case "preferred-align":
      return {
        Icon: Star,
        iconTestId: "star-icon",
        colorClass:
          "text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30",
      };
    case "feasible-align":
      return {
        Icon: CheckCircle2,
        iconTestId: "check-icon",
        colorClass:
          "text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/30",
      };
    case "no-group-match":
    default:
      return {
        Icon: Info,
        iconTestId: "info-icon",
        colorClass:
          "text-sky-600 hover:bg-sky-100 dark:text-sky-400 dark:hover:bg-sky-900/30",
      };
  }
}

function buildAriaLabel({ kind, reason, resolveEventTitle }) {
  if (kind === "conflict-override-urgent") {
    const prev = reason?.previousManualVote;
    return prev
      ? `Urgent: engine cleared your ${prev} vote due to a conflict`
      : "Urgent: engine cleared your vote due to a conflict";
  }
  if (kind === "no-group-match") {
    return reason?.humanLabel || "Mark not applied (group scope)";
  }
  if (kind === "preferred-align") {
    // Privacy (P2): mark.title is user-authored — keep. Event title is
    // resolved client-side via enrichment; until resolved, fall back to a
    // structural label rather than humanLabel (which is also structural for
    // event-sourced alignments now).
    const markTitle = reason?.mark?.title || null;
    const eventTitle =
      typeof resolveEventTitle === "function"
        ? resolveEventTitle(reason?.event)
        : null;
    const t = markTitle || eventTitle;
    return t ? `Preferred: ${t}` : "Preferred";
  }
  if (kind === "feasible-align") {
    const markTitle = reason?.mark?.title || null;
    const eventTitle =
      typeof resolveEventTitle === "function"
        ? resolveEventTitle(reason?.event)
        : null;
    const t = markTitle || eventTitle;
    return t ? `Feasible: ${t}` : "Feasible";
  }
  // Phase 3a-cascade: rsvp-cascade reasons name the originating session so
  // the user sees "this is my own booking" instead of a generic conflict.
  if (reason?.rsvpCascade?.sourceSchedulerLabel) {
    return `Booked: ${reason.rsvpCascade.sourceSchedulerLabel}`;
  }
  // conflict — also handle the legacy Chunk-C-shape (no events[], no
  // busyWindows[], just refId + humanLabel) by synthesizing a single entry
  // for label-counting purposes.
  const events = reason?.events || [];
  const busyWindows = reason?.busyWindows || [];
  const isLegacyShape =
    events.length === 0 &&
    busyWindows.length === 0 &&
    reason?.humanLabel &&
    reason?.refId;
  const synthCount = isLegacyShape ? 1 : 0;
  const totalCount = events.length + busyWindows.length + synthCount;
  if (totalCount === 0) {
    return reason?.humanLabel || "Conflict";
  }
  if (events.length === 1 && busyWindows.length === 0) {
    // Privacy (P2): resolved title comes from the enrichment cache.
    // Pre-P2 reasons may carry a legacy `title` field — resolveEventTitle
    // falls back to that. Nothing to enrich → structural label.
    const t =
      typeof resolveEventTitle === "function"
        ? resolveEventTitle(events[0])
        : events[0]?.title || null;
    return t ? `Conflict: ${t}` : "1 conflicting event";
  }
  if (isLegacyShape && reason?.type !== "busyWindow") {
    return `Conflict: ${reason.humanLabel}`;
  }
  return `${totalCount} conflicting event${totalCount === 1 ? "" : "s"}`;
}

// ---------------------------------------------------------------------------
// Body renderers
// ---------------------------------------------------------------------------

function ConflictBody({
  reason,
  schedulerId,
  slotId,
  displayTimeZone,
  resolveEventTitle,
}) {
  const events = reason?.events || [];
  const busyWindows = reason?.busyWindows || [];

  // Back-compat for Chunk-C-era reasons that lack `events[]` / `busyWindows[]`.
  // Synthesize a single row from `humanLabel` + `refId` so the popover still
  // renders a clickable link (no time range — that metadata wasn't captured
  // pre-D2). The next engine reeval widens the shape and replaces the synthetic.
  const isLegacyShape =
    events.length === 0 &&
    busyWindows.length === 0 &&
    reason?.humanLabel &&
    reason?.refId;
  const synthEvents =
    isLegacyShape && reason?.type !== "busyWindow"
      ? [
          {
            eventId: reason?.refId || null,
            // Privacy (P2): only legacy reasons reach this branch; their
            // humanLabel may still be the user-friendly title (engine reeval
            // will overwrite to a structural label on next pass). Until then,
            // showing the legacy label is the least-broken UX.
            title: reason?.humanLabel || "Conflict",
            startUtc: null,
            endUtc: null,
          },
        ]
      : [];
  const synthBusy =
    isLegacyShape && reason?.type === "busyWindow"
      ? [
          {
            sourceSchedulerId: reason?.refId || null,
            sourceSchedulerLabel: reason?.humanLabel || "Scheduled session",
            startUtc: null,
            endUtc: null,
          },
        ]
      : [];

  const effectiveEvents = events.length === 0 ? synthEvents : events;
  const effectiveBusy = busyWindows.length === 0 ? synthBusy : busyWindows;
  const totalCount = effectiveEvents.length + effectiveBusy.length;

  const combined = [
    ...effectiveEvents.map((e) => ({ kind: "event", payload: e })),
    ...effectiveBusy.map((b) => ({ kind: "busy", payload: b })),
  ];
  const visible = combined.slice(0, 3);
  const overflow = Math.max(combined.length - 3, 0);

  // Phase 3a-cascade: rsvp-cascade reasons name the originating session in
  // the popover headline so the user immediately recognizes their own
  // booking rather than reading "1 conflicting event".
  const rsvpCascadeLabel = reason?.rsvpCascade?.sourceSchedulerLabel || null;
  const headline = rsvpCascadeLabel
    ? `Booked: ${rsvpCascadeLabel}`
    : totalCount === 0
      ? "Conflict"
      : `${totalCount} conflicting event${totalCount === 1 ? "" : "s"}`;

  return (
    <>
      <p className="mb-2 font-semibold text-slate-700 dark:text-slate-200">
        {headline}
      </p>
      {totalCount === 0 ? (
        <p className="text-slate-600 dark:text-slate-300">{reason?.humanLabel}</p>
      ) : (
        <>
          <p className="mb-1.5 text-[11px] text-slate-500 dark:text-slate-400">
            Click an event to open it on My Availability.
          </p>
          <ul className="space-y-1">
            {visible.map((entry, idx) =>
              entry.kind === "event" ? (
                <EventRow
                  key={entry.payload.eventId || `evt-${idx}`}
                  event={entry.payload}
                  displayTimeZone={displayTimeZone}
                  resolveEventTitle={resolveEventTitle}
                />
              ) : (
                <BusyWindowRow
                  key={entry.payload.sourceSchedulerId || `busy-${idx}`}
                  window={entry.payload}
                  displayTimeZone={displayTimeZone}
                />
              ),
            )}
            {overflow > 0 && (
              <li>
                <Link
                  to={`/availability?slotConflict=${encodeURIComponent(`${schedulerId}:${slotId}`)}`}
                  className="block rounded px-2 py-1 text-brand-primary hover:bg-slate-100 hover:underline dark:text-blue-300 dark:hover:bg-slate-700"
                >
                  +{overflow} more
                </Link>
              </li>
            )}
          </ul>
        </>
      )}
    </>
  );
}

function AlignmentBody({ kind, reason, displayTimeZone, resolveEventTitle }) {
  // "Auto …" only when the auto-vote ENGINE derived this alignment (a vote was
  // actually cast/derived). An overlay-only alignment (reason.fromEngine ===
  // false) is just the user's own calendar matching this time — it must not
  // claim "Auto", so it reads plain "Preferred"/"Feasible". A real engine reason
  // (other SlotIndicator call sites) carries no `fromEngine`, so it stays "Auto".
  const level = kind === "preferred-align" ? "Preferred" : "Feasible";
  const headline = reason?.fromEngine === false ? level : `Auto ${level}`;
  // Either reason.mark or reason.event will be set (engine always populates
  // one for alignment kinds). The tooltip surfaces the source the engine
  // considered authoritative.
  if (reason?.mark) {
    const range = formatTimeRange(
      reason?.event?.startUtc,
      reason?.event?.endUtc,
      displayTimeZone,
    );
    return (
      <>
        <p className="mb-2 font-semibold text-slate-700 dark:text-slate-200">
          {headline}
        </p>
        <ul className="space-y-1">
          <MarkRow mark={reason.mark} range={range} />
        </ul>
      </>
    );
  }
  if (reason?.event) {
    return (
      <>
        <p className="mb-2 font-semibold text-slate-700 dark:text-slate-200">
          {headline}
        </p>
        <ul className="space-y-1">
          <EventRow
            event={reason.event}
            displayTimeZone={displayTimeZone}
            resolveEventTitle={resolveEventTitle}
          />
        </ul>
      </>
    );
  }
  // Defensive fallback — shouldn't be reachable from the engine but keeps
  // the indicator from rendering an empty popover if a malformed reason
  // sneaks through.
  return (
    <p className="text-slate-600 dark:text-slate-300">
      {reason?.humanLabel || headline}
    </p>
  );
}

function GroupScopeBody({ reason, groupNames }) {
  const markGroupIds = reason?.groupScope?.markGroupIds || [];
  const pollGroupId = reason?.groupScope?.pollGroupId || null;
  const markTitle = reason?.mark?.title || "Mark";
  const markId = reason?.mark?.markId || "";

  const markGroupNames =
    markGroupIds.length === 0
      ? "no group"
      : markGroupIds.map((id) => groupNames[id] || id).join(", ");
  const pollGroupName = pollGroupId
    ? groupNames[pollGroupId] || pollGroupId
    : "no group";

  return (
    <>
      <p className="mb-2 font-semibold text-slate-700 dark:text-slate-200">
        Mark not applied
      </p>
      <p className="text-slate-600 dark:text-slate-300">
        Your{" "}
        <Link
          to={`/availability?mark=${encodeURIComponent(markId)}`}
          className="font-medium text-brand-primary hover:underline dark:text-blue-300"
        >
          '{markTitle}'
        </Link>{" "}
        mark applies only to {markGroupNames}. This poll is in {pollGroupName}.
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// Row renderers
// ---------------------------------------------------------------------------

function EventRow({ event, displayTimeZone, resolveEventTitle }) {
  const range = formatTimeRange(event.startUtc, event.endUtc, displayTimeZone);
  // Privacy (P2): the title comes from the enrichment cache via the
  // SlotIndicator-level resolver. Fallback chain: enriched title → legacy
  // `event.title` (for stale pre-P2 reasons) → structural placeholder.
  const resolvedTitle =
    typeof resolveEventTitle === "function"
      ? resolveEventTitle(event)
      : event.title || null;
  const displayTitle = resolvedTitle || EVENT_TITLE_PLACEHOLDER;
  return (
    <li>
      <Link
        to={`/availability?event=${encodeURIComponent(event.eventId || "")}`}
        className="group flex items-start gap-2 rounded px-2 py-1.5 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset dark:hover:bg-slate-700 dark:focus-visible:bg-slate-700"
      >
        <span className="flex-1 min-w-0">
          <span className="block truncate font-medium text-slate-800 group-hover:text-brand-primary dark:text-slate-100 dark:group-hover:text-blue-300">
            {displayTitle}
          </span>
          {range && (
            <span className="block text-[11px] text-slate-500 dark:text-slate-400">
              {range}
            </span>
          )}
        </span>
        <ArrowUpRight
          className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-400 group-hover:text-brand-primary dark:text-slate-500 dark:group-hover:text-blue-300"
          aria-hidden="true"
        />
      </Link>
    </li>
  );
}

function BusyWindowRow({ window, displayTimeZone }) {
  const label = window.sourceSchedulerLabel || "Scheduled session";
  const range = formatTimeRange(window.startUtc, window.endUtc, displayTimeZone);
  return (
    <li>
      <Link
        to={`/scheduler/${encodeURIComponent(window.sourceSchedulerId || "")}`}
        className="group flex items-start gap-2 rounded px-2 py-1.5 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset dark:hover:bg-slate-700 dark:focus-visible:bg-slate-700"
      >
        <span className="flex-1 min-w-0">
          <span className="block truncate font-medium text-slate-800 group-hover:text-brand-primary dark:text-slate-100 dark:group-hover:text-blue-300">
            {label}
          </span>
          {range && (
            <span className="block text-[11px] text-slate-500 dark:text-slate-400">
              {range}
            </span>
          )}
        </span>
        <ArrowUpRight
          className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-400 group-hover:text-brand-primary dark:text-slate-500 dark:group-hover:text-blue-300"
          aria-hidden="true"
        />
      </Link>
    </li>
  );
}

function MarkRow({ mark, range }) {
  return (
    <li>
      <Link
        to={`/availability?mark=${encodeURIComponent(mark.markId || "")}`}
        className="group flex items-start gap-2 rounded px-2 py-1.5 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset dark:hover:bg-slate-700 dark:focus-visible:bg-slate-700"
      >
        <span className="flex-1 min-w-0">
          <span className="block truncate font-medium text-slate-800 group-hover:text-brand-primary dark:text-slate-100 dark:group-hover:text-blue-300">
            {mark.title || "(untitled mark)"}
          </span>
          {range && (
            <span className="block text-[11px] text-slate-500 dark:text-slate-400">
              {range}
            </span>
          )}
        </span>
        <ArrowUpRight
          className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-400 group-hover:text-brand-primary dark:text-slate-500 dark:group-hover:text-blue-300"
          aria-hidden="true"
        />
      </Link>
    </li>
  );
}

function formatTimeRange(startUtc, endUtc, tz) {
  if (!startUtc || !endUtc) return "";
  try {
    const startStr = formatZonedTime(new Date(startUtc), tz, "EEE MMM d, h:mm a", {
      showTimeZone: false,
    });
    const endStr = formatZonedTime(new Date(endUtc), tz, "h:mm a", {
      showTimeZone: false,
    });
    return `${startStr} – ${endStr}`;
  } catch {
    return "";
  }
}
